import React, { useState } from "react";
import { toast } from "sonner";
import { api, formatError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import ProfitField, { profitPayload } from "@/components/ProfitField";
import MemberReferralInfo from "@/components/MemberReferralInfo";
import RedeemConfirmDialog from "@/components/RedeemConfirmDialog";
import { Textarea } from "@/components/ui/textarea";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger,
} from "@/components/ui/dialog";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Coins, Plus, Minus } from "lucide-react";

/** Admin/Manager direct earn+redeem dialog, launched per-member from the
 *  Admin > Members table. Points credit IMMEDIATELY (per spec: "Points added
 *  directly by Admin do not need approval"). Managers go through the same
 *  path — the backend applies the admin-direct branch of /rewards/earn
 *  whenever `principal.role === 'admin'`. */
export default function MemberPointsDialog({ member, config, rates, onDone, trigger }) {
  const [open, setOpen] = useState(false);
  const [tab, setTab] = useState("earn");

  // Earn state
  const [earnMode, setEarnMode] = useState("default");
  const [earnService, setEarnService] = useState("Flights");
  const [earnAmount, setEarnAmount] = useState("");
  const [earnCurrency, setEarnCurrency] = useState(member?.preferred_currency || "AED");
  const [earnPoints, setEarnPoints] = useState("");
  const [earnReason, setEarnReason] = useState("");
  const [earnNotes, setEarnNotes] = useState("");
  const [earnProfit, setEarnProfit] = useState("");
  const [earnCounterparty, setEarnCounterparty] = useState("");

  // Redeem state
  const [redeemPts, setRedeemPts] = useState("");
  const [redeemNotes, setRedeemNotes] = useState("");

  const [busy, setBusy] = useState(false);

  const pv = Math.max(0.0001, parseFloat(config?.point_value_aed) || 1.0);
  const activeRule = earnMode === "service"
    ? ((config?.service_rules || {})[earnService] || config?.default_rule)
    : config?.default_rule;
  const previewPts = (() => {
    if (earnMode === "manual" || earnMode === "referral") return null;
    const amt = parseFloat(earnAmount);
    if (!amt || amt <= 0 || !activeRule || !rates?.length) return null;
    const rateDoc = rates.find((r) => r.currency === earnCurrency);
    const aed = earnCurrency === "AED"
      ? amt
      : Math.round(amt * (rateDoc?.rate_to_aed || 1) * 100) / 100;
    const rewardAed = activeRule.type === "percentage"
      ? aed * (activeRule.value / 100)
      : Number(activeRule.value);
    return { aed, pts: Math.round(rewardAed / pv) };
  })();

  const submitEarn = async (e) => {
    e.preventDefault();
    let finalPts = earnPoints !== ""
      ? parseInt(earnPoints, 10)
      : (previewPts?.pts ?? 0);
    if (!finalPts || finalPts <= 0) return toast.error("Points must be > 0");
    if (earnMode === "manual" && !earnReason.trim()) {
      return toast.error("Manual reward requires a Reason");
    }
    const payload = {
      customer_id: member.customer_id,
      mode: earnMode,
      final_points: finalPts,
      auto_calculated_points: previewPts?.pts ?? null,
      reason: earnReason.trim() || null,
      notes: earnNotes || null,
      force: false,
      ...profitPayload(earnProfit),
    };
    if (earnMode !== "manual" && earnMode !== "referral") {
      payload.purchase_amount = parseFloat(earnAmount);
      payload.currency = earnCurrency;
    }
    if (earnMode === "service") payload.service_type = earnService;

    setBusy(true);
    try {
      let { data } = await api.post("/rewards/earn", payload);
      if (data.requires_confirmation) {
        if (!window.confirm(data.message + " Proceed?")) { setBusy(false); return; }
        ({ data } = await api.post("/rewards/earn", { ...payload, force: true }));
      }
      toast.success(`+${data.transaction.final_points} pts · new balance ${data.new_balance}`);
      if (data.referral_bonuses?.length) {
        toast.success(`Referral bonus credited · ${data.referral_bonuses.length} entries`);
      }
      setEarnAmount(""); setEarnPoints(""); setEarnReason(""); setEarnNotes(""); setEarnCounterparty(""); setEarnProfit("");
      onDone?.(data);
      setOpen(false);
    } catch (err) {
      toast.error(formatError(err));
    } finally {
      setBusy(false);
    }
  };

  const [confirmRedeem, setConfirmRedeem] = useState(null);
  const submitRedeem = (e) => {
    e.preventDefault();
    const pts = parseInt(redeemPts, 10);
    if (!pts || pts <= 0) return toast.error("Points must be > 0");
    setConfirmRedeem({ points: pts, notes: redeemNotes });
  };
  const redeemDone = (data) => {
    setConfirmRedeem(null);
    setRedeemPts(""); setRedeemNotes("");
    onDone?.(data);
    setOpen(false);
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        {trigger || (
          <Button
            variant="outline"
            size="sm"
            className="rounded-full h-8"
            data-testid={`admin-points-btn-${member.customer_id}`}
          >
            <Coins className="h-3.5 w-3.5 mr-1.5" /> Points
          </Button>
        )}
      </DialogTrigger>
      <DialogContent className="max-w-xl" data-testid={`admin-points-dialog-${member.customer_id}`}>
        <DialogHeader>
          <DialogTitle>
            {member.name} · <span className="font-mono">{member.customer_id}</span>
            <span className="ml-2 label-caps">Balance {member.reward_balance} pts</span>
          </DialogTitle>
          {open && <MemberReferralInfo customerId={member.customer_id} refreshKey={`${member.reward_balance}-${member.lifetime_earned}`} />}
        </DialogHeader>
        <div className="rounded-xl p-3 text-xs mb-3"
             style={{ background: "rgba(16,185,129,0.08)", color: "var(--emerald)" }}>
          Points credit immediately — admin/manager earns don't need approval.
        </div>
        <Tabs value={tab} onValueChange={setTab}>
          <TabsList className="rounded-xl p-1"
            style={{ background: "var(--surface)", border: "1px solid var(--border)" }}>
            <TabsTrigger value="earn" data-testid="admin-points-tab-earn">
              <Plus className="h-3.5 w-3.5 mr-1.5" /> Add
            </TabsTrigger>
            <TabsTrigger value="redeem" data-testid="admin-points-tab-redeem">
              <Minus className="h-3.5 w-3.5 mr-1.5" /> Redeem
            </TabsTrigger>
          </TabsList>

          <TabsContent value="earn" className="pt-4">
            <form onSubmit={submitEarn} className="space-y-3" data-testid="admin-earn-form">
              <div className="flex items-center justify-between gap-2">
                <Label>Mode</Label>
              </div>
              <Select value={earnMode} onValueChange={(v) => { setEarnMode(v); setEarnPoints(""); setEarnCounterparty(""); }}>
                <SelectTrigger data-testid="admin-earn-mode"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="default">Default (total invoice)</SelectItem>
                  <SelectItem value="service">Service-Specific</SelectItem>
                  <SelectItem value="manual">Manual (goodwill / bonus)</SelectItem>
                </SelectContent>
              </Select>

              {earnMode === "service" && (
                <Select value={earnService} onValueChange={setEarnService}>
                  <SelectTrigger data-testid="admin-earn-service"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {["Flights","Hotels","Packages","Visa","Other"].map((s) => (
                      <SelectItem key={s} value={s}>{s}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              )}

              {earnMode !== "manual" && earnMode !== "referral" && (
                <div className="grid grid-cols-[1fr_120px] gap-2">
                  <div>
                    <Label>Invoice amount</Label>
                    <Input
                      type="number" min="0" step="0.01"
                      value={earnAmount}
                      onChange={(e) => { setEarnAmount(e.target.value); setEarnPoints(""); }}
                      required
                      data-testid="admin-earn-amount"
                    />
                  </div>
                  <div>
                    <Label>Currency</Label>
                    <Select value={earnCurrency} onValueChange={setEarnCurrency}>
                      <SelectTrigger data-testid="admin-earn-currency"><SelectValue /></SelectTrigger>
                      <SelectContent>
                        {rates.map((r) => (<SelectItem key={r.currency} value={r.currency}>{r.currency}</SelectItem>))}
                      </SelectContent>
                    </Select>
                  </div>
                </div>
              )}

              {earnMode !== "referral" && (
                <ProfitField value={earnProfit} onChange={setEarnProfit} currency={earnMode === "manual" ? "AED" : earnCurrency}
                             testid="admin-earn-profit" />
              )}

              {previewPts && (
                <div className="rounded-xl p-2 text-xs flex justify-between"
                     style={{ background: "var(--bg-soft)" }}>
                  <span style={{ color: "var(--text-muted)" }}>
                    AED {previewPts.aed.toLocaleString()} → {previewPts.pts} pts
                  </span>
                </div>
              )}

              <div>
                <Label>{earnMode === "manual" ? "Points to award" : "Final points (editable)"}</Label>
                <Input
                  type="number" min="1"
                  value={earnPoints !== "" ? earnPoints : (previewPts?.pts ?? "")}
                  onChange={(e) => setEarnPoints(e.target.value)}
                  placeholder="auto-calculated"
                  required={earnMode === "manual" || earnMode === "referral"}
                  data-testid="admin-earn-points"
                />
              </div>
              {(earnMode === "manual"
                || (previewPts && earnPoints !== "" && parseInt(earnPoints, 10) !== previewPts.pts)) && (
                <div>
                  <Label>Reason <span className="text-red-500">*</span></Label>
                  <Input
                    value={earnReason}
                    onChange={(e) => setEarnReason(e.target.value)}
                    required
                    data-testid="admin-earn-reason"
                  />
                </div>
              )}
              <div>
                <Label>Notes (optional)</Label>
                <Textarea
                  rows={2}
                  value={earnNotes}
                  onChange={(e) => setEarnNotes(e.target.value)}
                  data-testid="admin-earn-notes"
                />
              </div>
              <Button
                type="submit" disabled={busy}
                className="w-full bg-emerald-700 hover:bg-emerald-800 text-white rounded-xl h-11"
                data-testid="admin-earn-submit"
              >
                {busy ? "Processing…" : "Add points"}
              </Button>
            </form>
          </TabsContent>

          <TabsContent value="redeem" className="pt-4">
            <form onSubmit={submitRedeem} className="space-y-3" data-testid="admin-redeem-form">
              <RedeemConfirmDialog open={!!confirmRedeem} member={member} points={confirmRedeem?.points || 0} notes={confirmRedeem?.notes}
                                   onCancel={() => setConfirmRedeem(null)} onDone={redeemDone} />
              <div>
                <Label>Points to redeem</Label>
                <Input
                  type="number" min="1" max={member.reward_balance}
                  value={redeemPts}
                  onChange={(e) => setRedeemPts(e.target.value)}
                  required
                  data-testid="admin-redeem-points"
                />
                <p className="text-xs mt-1" style={{ color: "var(--text-muted)" }}>
                  Current balance: {member.reward_balance} pts
                </p>
              </div>
              <div>
                <Label>Notes (optional)</Label>
                <Textarea
                  rows={2}
                  value={redeemNotes}
                  onChange={(e) => setRedeemNotes(e.target.value)}
                  data-testid="admin-redeem-notes"
                />
              </div>
              <Button
                type="submit" disabled={busy}
                className="w-full text-navy rounded-xl h-11 hover:opacity-90"
                style={{ background: "var(--gold)" }}
                data-testid="admin-redeem-submit"
              >
                {busy ? "Processing…" : "Redeem points"}
              </Button>
            </form>
          </TabsContent>
        </Tabs>
      </DialogContent>
    </Dialog>
  );
}
